/**
 * cart-upsell
 * ---------------------------------------------------------------------------
 * "You may also like" in the cart drawer, from Shopify's own product
 * recommendations. Seeded from the first line in the cart, which the drawer
 * renders onto data-seed, so the list follows what is actually being bought.
 *
 * WHY THIS ELEMENT MOVES ITSELF BETWEEN TWO PARENTS.
 *
 * The two layouts need it in genuinely different places in the DOM:
 *
 *   desktop  outside the drawer, in the dimmed area to its left
 *   mobile   inside the drawer, under the cart lines
 *
 * The outside-left position cannot be done from inside the drawer content.
 * .cart-drawer__content is overflow:auto and .cart-drawer__inner is
 * overflow:hidden, so an absolutely positioned child is clipped by both no
 * matter what it is positioned against. The only element that can host it is
 * the dialog itself, which is overflow:visible.
 *
 * Rendering it twice was the alternative and is worse: two copies of four
 * product links in the accessibility tree. One element that relocates keeps a
 * single copy of the content.
 *
 * TWO WAYS INTO A CARD, AND THEY DO DIFFERENT THINGS.
 *
 *   the cart icon   adds straight to the cart
 *   anywhere else   opens the quick view
 *
 * The cart icon only adds directly when the product has exactly one variant.
 * With more than one there is nothing to add yet, because nobody has chosen a
 * size, so it opens the quick view on that product instead. Guessing the first
 * variant would put the wrong item in someone's cart.
 */

const BREAKPOINT = 990;

/* Fetch enough for the tallest case, then show as many as actually fit.
 *
 * THE LIMIT IS DRIVEN BY VIEWPORT HEIGHT, NOT WIDTH, because the desktop rail
 * is a vertical column. Width tells you nothing useful here: a 3440x1440
 * ultrawide and a 1920x1080 desktop differ by 360px of height, and that is the
 * dimension the cards consume.
 *
 * A card is a 180px square image plus about 50px of copy, and the rail adds 12
 * of gap, so roughly 242px each against the viewport height less about 76px of
 * the rail's own padding and heading:
 *
 *   1440 tall (ultrawide, 1440p)   5 cards, 1286px of 1364 available
 *   1080 tall (common desktop)     4 cards, 1028px of 1004, just over, so 4
 *   900 tall (MacBook class)       3 cards, 770px of 824
 *
 * Mobile is a horizontal scroller, so height does not bind and more can show.
 */
const FETCH_LIMIT = 8;
const MOBILE_VISIBLE = 6;

function visibleCount(isMobile) {
  if (isMobile) return MOBILE_VISIBLE;
  const h = window.innerHeight;
  if (h >= 1300) return 5;
  if (h >= 1000) return 4;
  return 3;
}

const CART_ICON =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" ' +
  'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
  '<path d="M4.2 5.3h2.1l1.9 9.4h9l1.8-6.9H7.3"/>' +
  '<circle cx="9.4" cy="18.6" r="1.4"/><circle cx="16.4" cy="18.6" r="1.4"/></svg>';

class CartUpsell extends HTMLElement {
  connectedCallback() {
    if (this.initialised) return;
    this.initialised = true;

    this.list = this.querySelector('[data-cu-list]');
    this.homeParent = this.parentElement;
    this.seed = null;
    this.products = [];

    this.media = window.matchMedia(`(max-width: ${BREAKPOINT - 1}px)`);
    this.handleBreakpoint = () => {
      this.place();
      this.render();
    };
    this.media.addEventListener('change', this.handleBreakpoint);

    // Only the two height thresholds are watched, not every resize, so nothing
    // re-renders until the count would actually change.
    this.heightQueries = [
      window.matchMedia('(min-height: 1300px)'),
      window.matchMedia('(min-height: 1000px)'),
    ];
    for (const q of this.heightQueries) q.addEventListener('change', this.handleBreakpoint);

    this.addEventListener('click', (event) => this.onClick(event));

    this.place();
    this.load();
  }

  disconnectedCallback() {
    if (this.media) this.media.removeEventListener('change', this.handleBreakpoint);
    for (const q of this.heightQueries || []) q.removeEventListener('change', this.handleBreakpoint);
  }

  /** Move into the in-drawer slot on mobile, back out to the dialog above it. */
  place() {
    const slot = document.querySelector('[data-cu-slot]');
    if (!slot || !this.homeParent) return;
    const wantsSlot = this.media.matches;
    const inSlot = this.parentElement === slot;
    if (wantsSlot && !inSlot) slot.appendChild(this);
    else if (!wantsSlot && inSlot) this.homeParent.appendChild(this);
  }

  async load() {
    const seed = this.dataset.seed;
    if (!seed || seed === this.seed) return;
    this.seed = seed;

    if (recsCache.has(seed)) {
      this.products = recsCache.get(seed);
      if (!this.products.length) { this.hidden = true; return; }
      this.render();
      this.hidden = false;
      return;
    }

    try {
      const url =
        '/recommendations/products.json?product_id=' + encodeURIComponent(seed) +
        '&limit=' + FETCH_LIMIT + '&intent=related';
      const res = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const data = await res.json();
      this.products = (data.products || []).filter((p) => p && p.available);
      recsCache.set(seed, this.products);
    } catch (error) {
      // A recommendation is a nicety. If it cannot be fetched the block stays
      // hidden rather than showing an error next to someone's checkout button.
      this.hidden = true;
      return;
    }

    if (!this.products.length) {
      this.hidden = true;
      return;
    }

    this.render();
    this.hidden = false;
  }

  /**
   * Built with the DOM rather than innerHTML, except the icon, which is our
   * own constant. Titles arrive over fetch, and textContent means a stray
   * angle bracket in one can never become markup.
   */
  render() {
    if (!this.list || !this.products.length) return;
    const frag = document.createDocumentFragment();
    const shown = this.products.slice(0, visibleCount(this.media.matches));

    shown.forEach((product, index) => {
      const li = document.createElement('li');
      li.className = 'cart-upsell__item';

      const card = document.createElement('div');
      card.className = 'cart-upsell__card';

      // The whole card opens the quick view. A button, not a link: it does not
      // navigate, and a link that does not navigate is a lie to the keyboard.
      const open = document.createElement('button');
      open.type = 'button';
      open.className = 'cart-upsell__open';
      open.dataset.cuOpen = String(index);
      open.setAttribute('aria-label', 'Quick view: ' + product.title);

      const media = document.createElement('span');
      media.className = 'cart-upsell__media';
      if (product.featured_image) {
        const img = document.createElement('img');
        img.src = product.featured_image.replace(/(\.[a-z]+)(\?|$)/i, '_300x$1$2');
        img.alt = '';
        img.loading = 'lazy';
        img.className = 'cart-upsell__img';
        media.appendChild(img);
      }
      open.appendChild(media);

      const body = document.createElement('span');
      body.className = 'cart-upsell__body';

      const name = document.createElement('span');
      name.className = 'cart-upsell__name';
      name.textContent = product.title;
      body.appendChild(name);

      const price = document.createElement('span');
      price.className = 'cart-upsell__price';
      price.textContent = this.formatPrice(product);
      body.appendChild(price);

      open.appendChild(body);
      card.appendChild(open);

      const add = document.createElement('button');
      add.type = 'button';
      add.className = 'cart-upsell__add';
      add.dataset.cuAdd = String(index);
      const single = product.variants && product.variants.length === 1;
      add.setAttribute(
        'aria-label',
        single ? 'Add ' + product.title + ' to cart' : 'Choose options for ' + product.title
      );
      add.innerHTML = CART_ICON;
      card.appendChild(add);

      li.appendChild(card);
      frag.appendChild(li);
    });

    this.list.replaceChildren(frag);
  }

  onClick(event) {
    const add = event.target.closest('[data-cu-add]');
    if (add) {
      event.preventDefault();
      this.quickAdd(this.products[Number(add.dataset.cuAdd)], add);
      return;
    }
    const open = event.target.closest('[data-cu-open]');
    if (open) {
      event.preventDefault();
      quickView.open(this.products[Number(open.dataset.cuOpen)]);
    }
  }

  async quickAdd(product, button) {
    if (!product) return;

    // Nothing to add until a variant is chosen. Picking one on the shopper's
    // behalf is how the wrong size ends up in a cart.
    if (!product.variants || product.variants.length !== 1) {
      quickView.open(product);
      return;
    }

    button.disabled = true;
    button.classList.add('is-busy');
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: product.variants[0].id, quantity: 1 }] }),
      });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      button.classList.add('is-done');
      refreshCart();
    } catch (error) {
      button.disabled = false;
      button.classList.remove('is-busy');
    }
  }

  /**
   * Prices arrive in cents. price_varies earns a "From" so a multi-variant
   * product does not appear to be its cheapest variant.
   */
  formatPrice(product) {
    return formatMoney(product.price, product.price_varies);
  }
}

/** Shared money formatting, used by the rail and the quick view. */
function formatMoney(cents, varies) {
  const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'ZAR';
  let amount;
  try {
    // narrowSymbol, because the default gives the ISO code: "ZAR 2,600.00"
    // where every other price on this store reads "R 2,600.00".
    amount = new Intl.NumberFormat('en', {
      style: 'currency',
      currency: currency,
      currencyDisplay: 'narrowSymbol',
    }).format(cents / 100);
    amount = amount.replace(/^([^\d\s]+)(\d)/, '$1 $2');
  } catch (error) {
    amount = (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }
  return varies ? 'From ' + amount : amount;
}

/**
 * Re-renders the cart drawer through the Section Rendering API, which is how
 * the rest of this theme refreshes it. Falls back to a reload only if the
 * theme's own cart component is not present.
 */
function refreshCart() {
  // No custom event is dispatched here. The theme listens on
  // StandardEvents.cartLinesUpdate from Shopify's own @shopify/events module,
  // not on anything we can name from theme code, and the 'cart:refresh' that
  // used to be dispatched had no listener anywhere in the theme.
  fetch('/?section_id=cart-drawer-section')
    .then((r) => r.text())
    .then((html) => {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('cart-drawer-component');
      const current = document.querySelector('cart-drawer-component');
      if (fresh && current) current.replaceWith(fresh);
    })
    .catch(() => {});
}

/**
 * QUICK VIEW
 * ---------------------------------------------------------------------------
 * Two drawers, the same relationship the cart drawer has with the upsell rail:
 * the buy panel on the right, its images in a separate drawer immediately left
 * of it.
 *
 * IT IS A MODAL <dialog>, AND IT HAS TO BE.
 *
 * A plain div on <body> with a high z-index works on desktop and fails on
 * mobile. The cart drawer opens with showModal() at that size, which puts it in
 * the browser's TOP LAYER, and the top layer paints above every z-indexed
 * element on the normal layer however large the number: z-index 60 went
 * underneath a drawer at z-index 20. Two top layer elements stack in promotion
 * order, so a dialog opened second is drawn above one opened first. Being a
 * real modal also brings Escape, focus trapping and inertness for free.
 *
 * THE SEQUENCE IS CSS, NOT JAVASCRIPT TIMERS.
 *
 *   opening   panel slides in, then the gallery follows on a delay
 *   closing   gallery leaves first, then the panel follows
 *
 * Both orders come from transition-delay on two classes, so the browser owns
 * the timing and the frames stay on the compositor. The single timer here is
 * the one that finally calls close(), because a dialog cannot be closed
 * mid-transition without the panel vanishing instantly.
 *
 * TWO CACHES. A product's JSON and its rendered selling points are both fetched
 * once per handle and kept, so reopening the same card costs nothing.
 */

const productCache = new Map();
const detailsCache = new Map();

/* Recommendations keyed by seed product. The drawer re-renders through the
 * Section Rendering API on every cart change, which constructs a fresh
 * <cart-upsell> each time, so without this the same list was re-fetched on
 * every quantity tap. */
const recsCache = new Map();

const quickView = {
  el: null,
  product: null,
  variant: null,

  mount() {
    if (this.el) return this.el;

    const el = document.createElement('dialog');
    el.className = 'cu-quickview';
    el.setAttribute('aria-label', 'Quick view');
    el.innerHTML =
      '<aside class="cu-quickview__gallery" data-qv-gallery aria-label="Product images"></aside>' +
      '<div class="cu-quickview__panel">' +
        '<div class="cu-quickview__head">' +
          '<p class="cu-quickview__eyebrow">Select options</p>' +
          '<button type="button" class="cu-quickview__close" data-qv-close aria-label="Close quick view">' +
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>' +
          '</button>' +
        '</div>' +
        '<div class="cu-quickview__body">' +
          '<h2 class="cu-quickview__title" data-qv-title></h2>' +
          '<p class="cu-quickview__price" data-qv-price></p>' +
          '<p class="cu-quickview__tax">Tax included. Shipping calculated at checkout.</p>' +
          '<div class="cu-quickview__points" data-qv-points></div>' +
          '<div data-qv-options></div>' +
          '<div class="cu-quickview__buy">' +
            '<div class="cu-quickview__qty">' +
              '<button type="button" data-qv-minus aria-label="Decrease quantity">&minus;</button>' +
              '<input type="number" value="1" min="1" data-qv-qty aria-label="Quantity">' +
              '<button type="button" data-qv-plus aria-label="Increase quantity">+</button>' +
            '</div>' +
            '<button type="button" class="cu-quickview__atc" data-qv-add>' + CART_ICON + '<span>Add to cart</span></button>' +
          '</div>' +
          '<button type="button" class="cu-quickview__buynow" data-qv-buy>Buy it now</button>' +
          '<p class="cu-quickview__status" data-qv-status role="status" aria-live="polite" hidden></p>' +
        '</div>' +
      '</div>';

    document.body.appendChild(el);

    el.addEventListener('click', (event) => {
      // A modal dialog fills the viewport, so a click landing on the dialog
      // itself rather than on either drawer is a click on the backdrop.
      if (event.target === el) return this.close();
      if (event.target.closest('[data-qv-close]')) return this.close();
      if (event.target.closest('[data-qv-add]')) return this.add(false);
      if (event.target.closest('[data-qv-buy]')) return this.add(true);
      if (event.target.closest('[data-qv-minus]')) return this.step(-1);
      if (event.target.closest('[data-qv-plus]')) return this.step(1);
      const opt = event.target.closest('[data-qv-variant]');
      if (opt) this.select(Number(opt.dataset.qvVariant));
    });

    // Escape fires the dialog's own cancel. Taken over so the panel animates
    // out rather than disappearing on the spot.
    el.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.close();
    });

    this.el = el;
    return el;
  },

  async open(seed) {
    if (!seed) return;
    const el = this.mount();

    // Painted from the recommendation payload straight away, so the panel is
    // never briefly empty. The fetches below only fill in what it lacks.
    this.product = seed;
    this.variant = (seed.variants || []).find((v) => v.available) || (seed.variants || [])[0] || null;
    this.paint(el);

    if (!el.open) el.showModal();
    const close = el.querySelector('.cu-quickview__close');
    if (close) close.focus();

    const handle = seed.handle;
    const [full, details] = await Promise.all([this.fetchProduct(handle), this.fetchDetails(handle)]);

    // Guard against a second card being opened while these were in flight.
    if (!this.product || this.product.handle !== handle) return;

    if (full) {
      this.product = full;
      this.variant = (full.variants || []).find((v) => v.available) || (full.variants || [])[0] || null;
      this.paint(el);
    }
    if (details) el.querySelector('[data-qv-points]').innerHTML = details;
  },

  async fetchProduct(handle) {
    if (productCache.has(handle)) return productCache.get(handle);
    try {
      const res = await fetch('/products/' + encodeURIComponent(handle) + '.js');
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const json = await res.json();
      productCache.set(handle, json);
      return json;
    } catch (error) {
      return null;
    }
  },

  /** The overview and bullets, rendered server side from their metafields. */
  async fetchDetails(handle) {
    if (detailsCache.has(handle)) return detailsCache.get(handle);
    try {
      const res = await fetch(
        '/products/' + encodeURIComponent(handle) + '?section_id=cu-quickview-details'
      );
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const html = await res.text();
      // The Section Rendering API wraps output in its own section div.
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const inner = doc.body.firstElementChild ? doc.body.firstElementChild.innerHTML : '';
      detailsCache.set(handle, inner);
      return inner;
    } catch (error) {
      return null;
    }
  },

  close() {
    const el = this.el;
    if (!el || !el.open) return;
    // Closed outright. There is no exit transition left to wait for, so the
    // dialog must not linger holding the page inert.
    el.close();
    el.querySelector('[data-qv-points]').replaceChildren();
    this.product = null;
  },

  paint(el) {
    const p = this.product;
    el.querySelector('[data-qv-title]').textContent = p.title;

    const gallery = el.querySelector('[data-qv-gallery]');
    const images = (p.images && p.images.length ? p.images : [p.featured_image]).filter(Boolean).slice(0, 5);
    const frag = document.createDocumentFragment();
    for (const src of images) {
      const fig = document.createElement('figure');
      fig.className = 'cu-quickview__shot';
      const img = document.createElement('img');
      img.src = String(src).replace(/(\.[a-z]+)(\?|$)/i, '_540x$1$2');
      img.alt = '';
      img.loading = 'lazy';
      img.decoding = 'async';
      fig.appendChild(img);
      frag.appendChild(fig);
    }
    gallery.replaceChildren(frag);
    gallery.hidden = images.length === 0;

    this.renderOptions(el);
    this.syncPrice(el);

    const status = el.querySelector('[data-qv-status]');
    status.hidden = true;
    status.textContent = '';
    el.querySelector('[data-qv-qty]').value = '1';
  },

  /**
   * One button per variant. The theme's own picker works on option
   * combinations, but a quick view only ever shows a handful, and a flat list
   * cannot land on a combination that does not exist.
   */
  renderOptions(el) {
    const wrap = el.querySelector('[data-qv-options]');
    const p = this.product;
    if (!p.variants || p.variants.length < 2) {
      wrap.replaceChildren();
      return;
    }

    const frag = document.createDocumentFragment();
    const label = document.createElement('p');
    label.className = 'cu-quickview__optlabel';
    const first = p.options && p.options[0];
    label.textContent = (first && (first.name || first)) || 'Options';
    frag.appendChild(label);

    const row = document.createElement('div');
    row.className = 'cu-quickview__opts';
    p.variants.forEach((v, i) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'cu-quickview__opt';
      b.dataset.qvVariant = String(i);
      b.textContent = v.title;
      if (!v.available) b.disabled = true;
      if (this.variant && v.id === this.variant.id) b.setAttribute('aria-current', 'true');
      row.appendChild(b);
    });
    frag.appendChild(row);
    wrap.replaceChildren(frag);
  },

  select(index) {
    const v = (this.product.variants || [])[index];
    if (!v || !v.available) return;
    this.variant = v;
    this.renderOptions(this.el);
    this.syncPrice(this.el);
  },

  syncPrice(el) {
    const price = this.variant ? this.variant.price : this.product.price;
    el.querySelector('[data-qv-price]').textContent = formatMoney(price, false);
  },

  step(direction) {
    const input = this.el.querySelector('[data-qv-qty]');
    input.value = String(Math.max(1, (parseInt(input.value, 10) || 1) + direction));
  },

  async add(thenCheckout) {
    if (!this.variant) return;
    const status = this.el.querySelector('[data-qv-status]');
    const qty = Math.max(1, parseInt(this.el.querySelector('[data-qv-qty]').value, 10) || 1);

    status.hidden = false;
    status.textContent = 'Adding...';
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: this.variant.id, quantity: qty }] }),
      });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      if (thenCheckout) {
        window.location.href = '/checkout';
        return;
      }
      status.textContent = 'Added to your cart.';
      refreshCart();
      setTimeout(() => this.close(), 700);
    } catch (error) {
      status.textContent = 'That did not add. Please try again.';
    }
  },
};

if (!customElements.get('cart-upsell')) {
  customElements.define('cart-upsell', CartUpsell);
}

/**
 * KEEPING THE RAIL ALIVE ACROSS CART CHANGES
 * ---------------------------------------------------------------------------
 * The rail used to appear only after navigating to another page. Two separate
 * routes lead there, and both end with no <cart-upsell> in the document:
 *
 *   1. Someone who lands with an empty cart is never served the element at
 *      all. It is gated on the cart having items, and the drawer's outer
 *      markup is only built on a full page load, so adding the first item
 *      never produces one.
 *
 *   2. place() parks the element in .cart-upsell__slot, and that slot lives
 *      INSIDE <cart-items-component>. The theme re-renders exactly that
 *      component on every cart change, so the rail it is holding is destroyed
 *      along with it.
 *
 * refreshCart() below would fix both by replacing the whole drawer, but it is
 * only reached from this file's own quick-view flow; the theme's normal
 * add-to-cart does not call it. It also throws away drawer state, which is too
 * blunt for something the shopper may be looking at.
 *
 * shopify:cart:lines-update is the theme's own signal. It is dispatched on
 * product-form-component and bubbles to the document, and every other cart
 * component in the theme listens for it, so it is the correct moment to
 * repair either case: re-seed the rail if one survived, graft a fresh one in
 * from the section endpoint if it did not.
 */
let syncing = false;

function syncUpsell() {
  if (syncing) return;
  syncing = true;
  fetch('/cart.js', { headers: { Accept: 'application/json' } })
    .then((r) => r.json())
    .then((cart) => {
      const existing = document.querySelector('cart-upsell');

      if (!cart.item_count) {
        // Nothing to recommend against. Hide rather than remove, so an
        // element that is still in the tree is reused on the next add.
        if (existing) existing.hidden = true;
        return null;
      }

      const seed = String(cart.items[0].product_id);

      if (existing) {
        // load() is already idempotent: it returns early when the seed has
        // not moved, and refetches when it has.
        existing.dataset.seed = seed;
        if (typeof existing.load === 'function') existing.load();
        return null;
      }

      return fetch('/?section_id=cart-drawer-section')
        .then((r) => r.text())
        .then((html) => {
          // Another sync may have won the race while this was in flight.
          if (document.querySelector('cart-upsell')) return;

          const doc = new DOMParser().parseFromString(html, 'text/html');
          const fresh = doc.querySelector('cart-upsell');
          // The dialog, not the slot: place() moves it into the slot itself at
          // mobile widths, and the slot is the thing that keeps getting wiped.
          const dialog = document.querySelector('cart-drawer-component')?.closest('dialog');
          // isConnected matters: the theme swaps the drawer out while handling
          // the same event, so the dialog found a moment ago can already be
          // detached. Appending to it would put the rail in an orphan tree,
          // where it is upgraded, seeded, and invisible forever.
          if (!fresh || !dialog || !dialog.isConnected) return;

          fresh.dataset.seed = seed;
          // Upgrades on insertion, because the class is defined above.
          dialog.appendChild(document.importNode(fresh, true));
        });
    })
    .catch(() => {})
    .finally(() => {
      syncing = false;
    });
}

document.addEventListener('shopify:cart:lines-update', () => {
  // The theme rebuilds the drawer while handling this same event, so a single
  // attempt is a race it frequently loses: the rail is either destroyed with
  // the subtree it was grafted into, or grafted into a dialog that is already
  // detached. Measured on the live store, a lone 350ms attempt produced no
  // rail at all.
  //
  // So: one immediate pass to re-seed a rail that survived, then a short
  // backoff that only fires while there is still no rail in the document.
  // Bounded, and it stops as soon as one exists.
  //
  // Every pass runs unconditionally. An earlier version skipped later passes
  // whenever a rail already existed, which fixed the missing-rail case and
  // quietly broke the other one: the first pass can read /cart.js before the
  // add has committed, seed from the previous first line, and then never be
  // corrected. Shopify puts the newest line first, so that is exactly the
  // value the rail should be following.
  //
  // Re-running is cheap and self-correcting. load() returns early when the
  // seed has not moved, and the graft path re-checks for an existing rail, so
  // the extra passes cost a small same-origin /cart.js and nothing else.
  for (const ms of [350, 1200, 2500]) setTimeout(syncUpsell, ms);
});
